import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { BookOpen, Layers, Plus, Sparkles, X, Zap } from 'lucide-react';
import React from 'react';

interface TimelineGuidedSetupProps {
    onApplyTemplate: (templateId: string) => void;
    onStartFresh: () => void;
    onClose: () => void;
    isApplying?: boolean;
}

export const TimelineGuidedSetup: React.FC<TimelineGuidedSetupProps> = ({
    onApplyTemplate,
    onStartFresh,
    onClose,
    isApplying = false,
}) => {
    const featuredTemplates = [
        { id: 'hero', name: "Hero's Journey", description: "12 steps of discovery.", icon: <Sparkles className="h-4 w-4" /> },
        { id: 'threeact', name: "Three-Act Structure", description: "Classic story arc.", icon: <Layers className="h-4 w-4" /> },
        { id: 'savecat', name: "Save the Cat", description: "Standard beat sheet.", icon: <BookOpen className="h-4 w-4" /> },
        { id: 'snowflake', name: "Snowflake Method", description: "Design from the core.", icon: <Zap className="h-4 w-4" /> },
    ];

    return (
        <div className="w-full mb-10">
            <Card className="bg-card border-border shadow-sm transition-all duration-200 overflow-hidden flex flex-col rounded-2xl group/card relative">
                <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4 h-8 w-8 rounded-full text-muted-foreground hover:text-foreground transition-colors z-10"
                    onClick={onClose}
                >
                    <X className="h-4 w-4" />
                </Button>

                <CardContent className="p-0 flex-1 relative bg-transparent">
                    <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-border/50">
                        {/* Main Content Area */}
                        <div className="flex-1 p-10 space-y-8">
                            <div className="space-y-3">
                                <h2 className="text-3xl font-extrabold tracking-tight text-foreground leading-tight">
                                    Build your story foundations <br />
                                    <span className="text-primary italic font-serif">in one shot.</span>
                                </h2>
                                <p className="text-muted-foreground text-sm max-w-xl leading-relaxed">
                                    Choose a storytelling structure to generate your timeline, chapters, and key beats instantly.
                                    Focus on your craft while we handle the framework.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                                {featuredTemplates.map((template) => (
                                    <button
                                        key={template.id}
                                        onClick={() => onApplyTemplate(template.id)}
                                        disabled={isApplying}
                                        className={cn(
                                            "flex items-center gap-4 p-5 rounded-2xl border border-border bg-muted/5 hover:bg-muted/10 hover:border-primary/40 transition-all text-left group/item",
                                            isApplying && "opacity-50 cursor-not-allowed"
                                        )}
                                    >
                                        <div className="h-11 w-11 rounded-xl bg-primary/5 flex items-center justify-center text-primary group-hover/item:bg-primary group-hover/item:text-primary-foreground transition-all shrink-0 shadow-sm">
                                            {template.icon}
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-sm font-bold group-hover/item:text-primary transition-colors">{template.name}</span>
                                            <span className="text-[11px] text-muted-foreground leading-tight">{template.description}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Sidebar / Quick Action Area */}
                        <div className="w-full lg:w-80 p-10 bg-muted/5 flex flex-col justify-between space-y-12">
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Start Fresh</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        Already have a vision? Skip the templates and build your custom structure from scratch.
                                    </p>
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={onStartFresh}
                                    disabled={isApplying}
                                    className="w-full rounded-xl h-11 border-border bg-background hover:bg-muted/10 font-bold transition-all shadow-sm group/btn"
                                >
                                    <Plus className="h-4 w-4 mr-2 group-hover/btn:rotate-90 transition-transform" />
                                    Blank Canvas
                                </Button>
                            </div>

                            <div className="pt-8 border-t border-border/30">
                                <div className="space-y-3">
                                    <div className="h-1 w-8 bg-primary/20 rounded-full" />
                                    <p className="text-[11px] text-muted-foreground italic leading-relaxed font-serif">
                                        "A story is not just what happens, but the order in which it is told."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Progress Overlay */}
                    {isApplying && (
                        <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] z-30 flex flex-col items-center justify-center">
                            <div className="bg-card border border-border/50 shadow-2xl p-8 rounded-3xl flex flex-col items-center gap-4 text-center max-w-xs animate-in zoom-in-95 duration-300">
                                <div className="h-12 w-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                                <div className="space-y-1">
                                    <h3 className="text-lg font-bold">Generating Structure</h3>
                                    <p className="text-muted-foreground text-xs">Architecting your story universe...</p>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
